// Typed calls to the server. Every screen goes through here.
import type { Cents, LeftoverMode, ParsedReceipt, Place, PublicSplit } from '../types.ts';
import { brand } from '../brand.ts';
import { trackHeaders } from './track.ts';
import { ls } from './dom.ts';

/** An error the server answered with. `status` 402 at lock-in means the payment didn't go through. */
export class ApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) { super(message); this.status = status; }
}

// The payer's sign-in, from the text code. Friends never have one.
const SESSION_KEY = 'session';
export const session = {
  get: (): string => ls.get<string>(SESSION_KEY, ''),
  set: (t: string): void => ls.set(SESSION_KEY, t),
};

export async function request<T>(path: string, body?: unknown, extra: Record<string, string> = {}): Promise<T | null> {
  const token = session.get();
  const headers = { ...trackHeaders(), ...(token ? { 'x-session': token } : {}), ...extra };
  const opts: RequestInit = body !== undefined
    ? { method: 'POST', headers: { ...headers, 'content-type': 'application/json' }, body: JSON.stringify(body) }
    : { headers };
  let r: Response;
  try {
    r = await fetch(path, opts);
  } catch {
    throw new Error(`Can't reach ${brand.name}. Check your connection and try again.`);
  }
  noteVersion(r.headers.get('x-app-version'));
  if (r.status === 204) return null;
  let data: unknown = {};
  try { data = await r.json(); } catch { /* empty body */ }
  if (!r.ok) throw new ApiError((data as { error?: string }).error || 'Something went wrong. Try again.', r.status);
  return data as T;
}

// After a deploy the server reports a new app version. Reload to pick it up, but only when it
// can't cost anyone anything: not mid-receipt (that draft lives only in this page), not while a
// sheet is open, and not while someone is typing or has typed into a box.
let firstVersion: string | null = null;
let updateWaiting = false;
function noteVersion(v: string | null): void {
  if (!v) return;
  if (!firstVersion) firstVersion = v;
  else if (v !== firstVersion) updateWaiting = true;
  if (updateWaiting && safeToReload()) location.reload();
}
function safeToReload(): boolean {
  if (location.pathname.startsWith('/new')) return false;
  if (document.querySelector('.sheet')) return false;
  return ![...document.querySelectorAll('input, textarea, select')].some(
    (el) => el === document.activeElement || ((el as HTMLInputElement).value || '').trim() !== '',
  );
}

async function must<T>(path: string, body?: unknown, extra?: Record<string, string>): Promise<T> {
  return (await request<T>(path, body, extra)) as T;
}

export interface PayerDetails { name: string; venmo: string; cashapp: string; zelle: string }
export interface NewSplit {
  venue: string;
  date: string;
  items: Array<{ name: string; cents: Cents }>;
  taxCents: Cents;
  taxLabel: string;
  /** Named fees that are included in taxCents, for display. */
  fees: Array<{ name: string; cents: Cents }>;
  tipCents: Cents;
  tipLabel: string;
  tipSource?: 'receipt' | 'host_pick' | 'no_tip';
  thanksOn: boolean;
  /** The host tapped the $1 thanks switch on themselves. */
  thanksPicked?: boolean;
  partySize: number;
  /** Split the whole bill evenly across partySize. */
  even?: boolean;
  payer: PayerDetails;
  /** Where the restaurant is, from the receipt. */
  place?: Place | null;
  /** A promo or referral code typed in. Codes from links are applied by the server. */
  refCode?: string;
}
export interface Membership { personId: string; token: string; name?: string }

const base = (code: string) => `/api/splits/${encodeURIComponent(code)}`;

export interface AppVersion { commit: string; message: string; startedAt: string; app: string }
export const getConfig = () => must<{ receiptReading: boolean; version?: AppVersion; fee?: import('../experiments.ts').FeeConfig | { on: false }; cardsKey?: string; pricing?: boolean; sms?: boolean }>('/api/config');
export const parseReceipt = (image: string, mediaType: string) => must<ParsedReceipt>('/api/parse', { image, mediaType });
/** How the split got made, for the stats: scanned or typed, and seconds from start to share. */
export interface Flow { source: 'scan' | 'manual'; secs: number; rereads: number }
export const createSplit = (s: NewSplit, flow?: Flow) =>
  must<Membership & { code: string; adminKey: string }>('/api/splits', s, flow ? { 'x-flow': encodeURIComponent(JSON.stringify(flow)) } : {});
export interface PartnerStats { code: string; label: string; revSharePct: number; link: string; terms: string; offer: string; rewardSplits: number; payerCap: number; visits: number; splits: number; friendsReached: number; newPayers: number; earnedCents: number; feesLive: boolean }
/** The calculator's "Email me my results". Saved for the owner; nothing is emailed automatically. */
export const sendCalcLead = (lead: { email: string; restaurant: string; name: string; optIn: boolean; inputs: Record<string, string>; utm: Record<string, string> }) =>
  must<{ ok: true }>('/api/restaurant-calc', lead);
export const sendRestaurantLead = (lead: { restaurant: string; name: string; contact: string; message: string }) =>
  must<{ ok: true; locations: number }>('/api/contact', lead);
export const partnerStats = (token: string) => must<PartnerStats>(`/api/partner/${encodeURIComponent(token)}`);

export const checkCode = (code: string) => must<{ code: string; kind: string; offer: string; own?: boolean; venue?: string; link?: string }>(`/api/codes/${encodeURIComponent(code)}`);

const asAdmin = (token: string) => ({ authorization: 'Bearer ' + token });
export const admin = {
  users: (t: string) => must<import('../users.ts').Users>('/api/admin/users', undefined, asAdmin(t)),
  venues: (t: string) => must<{ venues: ReturnType<typeof import('../venues.ts').venueList> }>('/api/admin/venues', undefined, asAdmin(t)),
  venue: (t: string, key: string) => must<import('../venues.ts').VenueDetail>(`/api/admin/venues/${encodeURIComponent(key)}`, undefined, asAdmin(t)),
  leads: (t: string) => must<{ leads: import('../leads.ts').LeadRow[]; calcLeads: import('../leads.ts').CalcLeadRow[] }>('/api/admin/leads', undefined, asAdmin(t)),
  makeCards: (t: string, lead: string) => must<{ cards: import('../leads.ts').CardCode[] }>(`/api/admin/leads/${encodeURIComponent(lead)}`, {}, asAdmin(t)),
  codes: (t: string) => must<{ codes: AdminCode[]; feeNetCents: number }>('/api/admin/codes', undefined, asAdmin(t)),
  createCode: (t: string, c: Partial<Pick<AdminCode, 'code' | 'label' | 'kind' | 'offer' | 'ownerKey' | 'ownerName' | 'revSharePct' | 'freeSplits' | 'rewardSplits' | 'payerCap' | 'fixedFee'>> & { utm?: Partial<AdminCode['utm']> }) =>
    must<AdminCode>('/api/admin/codes', c, asAdmin(t)),
  setCodeActive: (t: string, code: string, active: boolean) => must(`/api/admin/codes/${encodeURIComponent(code)}`, { active }, asAdmin(t)),
  splits: (t: string) => must<{ splits: import('../test-splits.ts').SplitRow[] }>('/api/admin/splits', undefined, asAdmin(t)),
  experiments: (t: string, source = '') => must<import('../experiment-stats.ts').ExperimentResult>(`/api/admin/experiments${source ? `?source=${encodeURIComponent(source)}` : ''}`, undefined, asAdmin(t)),
  setTest: (t: string, code: string, test: boolean) => must<{ code: string; test: boolean }>(`/api/admin/splits/${encodeURIComponent(code)}`, { test }, asAdmin(t)),
};
export type AdminCode = ReturnType<typeof import('../codes.ts').codeStats>[number] & { link: string };

export const adminStats = (token: string, days = 30) =>
  must<import('../stats.ts').Stats & { health: import('../health.ts').Health }>(`/api/admin/stats?days=${days}`, undefined, { authorization: 'Bearer ' + token });

/** Null means nothing changed since `version`. */
export const getSplit = (code: string, version: number, key?: string | null) =>
  request<PublicSplit>(`${base(code)}?v=${version}${key ? `&key=${encodeURIComponent(key)}` : ''}`);

export const friend = {
  join: (code: string, body: { name?: string; phone?: string; remind?: boolean; personId?: string; token?: string; tz?: string; via?: string }) => must<Membership>(`${base(code)}/join`, body),
  claim: (code: string, token: string, itemId: string, on: boolean) => must(`${base(code)}/claim`, { token, itemId, on }),
  share: (code: string, token: string, itemId: string, withIds: string[]) => must(`${base(code)}/share`, { token, itemId, with: withIds }),
  answerMark: (code: string, token: string, itemId: string, keep: boolean) => must(`${base(code)}/mark`, { token, itemId, keep }),
  setPaid: (code: string, token: string, paid: boolean, how?: string) => must(`${base(code)}/paid`, { token, paid, how }),
  /** Tapped Pay with Venmo or Cash App. Fire and forget, kept alive while the page goes behind the app. */
  paying: (code: string, token: string, app: 'venmo' | 'cashapp') => must(`${base(code)}/paying`, { token, app }),
  optIn: (code: string, token: string, on: boolean, phone?: string) => must(`${base(code)}/optin`, { token, on, phone }),
  /** This friend's own referral code; saves `phone` first if they had none. */
  myCode: (code: string, token: string, phone?: string) => must<{ code?: string; link?: string; needPhone?: boolean; off?: boolean }>(`${base(code)}/mycode`, { token, phone }),
  /** "Ask <Host> to lock it in": shows on the host's board. Once a minute; never a text. */
  nudge: (code: string, token: string) => must(`${base(code)}/nudge`, { token }),
  survey: (code: string, token: string, answer: { rating?: number; occasion?: string; comeBack?: string }) => must(`${base(code)}/survey`, { token, ...answer }),
};

export type PayerAction =
  | { action: 'setThanks'; on: boolean }
  | { action: 'close'; leftover: { mode: LeftoverMode; assignTo?: string } }
  | { action: 'reopen' }
  | { action: 'setPaid'; personId: string; paid: boolean }
  | { action: 'forgive'; personId: string; forgiven: boolean }
  | { action: 'addPerson'; name: string; phone?: string }
  | { action: 'setPhone'; personId: string; phone: string }
  | { action: 'setPartySize'; size: number }
  | { action: 'setEven'; on: boolean; size?: number }
  | { action: 'claimFor'; personId: string; itemId: string; on: boolean };

export const payer = (code: string, key: string, a: PayerAction) => must<unknown>(`${base(code)}/admin`, { key, ...a });

/** "Lock it in". Answers `pay` when a fee is owed: confirm it with Stripe, then call again with its id. */
export interface LockReply { locked: boolean; processing?: boolean; pay?: { clientSecret: string; publishableKey: string; cents: Cents } }
export const lockSplit = (code: string, key: string, paymentIntent?: string) => must<LockReply>(`${base(code)}/lock`, { key, ...(paymentIntent ? { paymentIntent } : {}) });

/** Poll a split every few seconds while the page is visible. The server answers 204 when nothing changed. */
export function poll(code: string, key: string | null, onData: (s: PublicSplit | null, err: Error | null) => void) {
  let version = 0;
  let alive = true;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const tick = async (): Promise<void> => {
    clearTimeout(timer);
    if (!alive) return;
    if (document.visibilityState === 'visible' || !version) {
      try {
        const s = await getSplit(code, version, key);
        if (s && alive) { version = s.version; onData(s, null); }
      } catch (err) {
        if (alive && !version) onData(null, err as Error);
      }
    }
    if (alive) timer = setTimeout(tick, 2500);
  };
  const onVisible = () => { if (document.visibilityState === 'visible') void tick(); };
  document.addEventListener('visibilitychange', onVisible);
  void tick();
  return {
    stop(): void { alive = false; clearTimeout(timer); document.removeEventListener('visibilitychange', onVisible); },
    refresh: tick,
  };
}
export type Poller = ReturnType<typeof poll>;

// ---------- the payer's phone sign-in and saved card ----------
export interface AccountView { phoneLast4: string; card: { brand: string; last4: string } | null }
export const sendCode = (phone: string) => must<{ sent: boolean; phoneLast4: string }>('/api/auth/start', { phone });
export const checkPhoneCode = (phone: string, code: string) => must<AccountView & { session: string }>('/api/auth/check', { phone, code });
